import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

type NavigationDirection = 'back' | 'forward';

export function useBackButtonDetection(
  callback?: (direction: NavigationDirection, path: string) => void
) {
  const routeHistory = ref<string[]>([]);
  const currentIndex = ref(0);
  const isPopState = ref(false);

  const route = useRoute();

  const handlePopState = () => {
    isPopState.value = true;
  };

  onMounted(() => {
    if (!route || !route.fullPath) {
      console.warn('[useBackButtonDetection] Route not ready');
      return;
    }

    // Initial route
    routeHistory.value.push(route.fullPath);
    currentIndex.value = 0;

    window.addEventListener('popstate', handlePopState);
  });

  onUnmounted(() => {
    window.removeEventListener('popstate', handlePopState);
  });

  watch(
    () => route.fullPath,
    (newPath) => {
      const history = routeHistory.value;

      if (isPopState.value) {
        const newIndex = history.indexOf(newPath);
        if (newIndex < currentIndex.value) {
          // Back
          currentIndex.value = newIndex;
          callback?.('back', newPath);
        } else if (newIndex > currentIndex.value) {
          // Forward
          currentIndex.value = newIndex;
          callback?.('forward', newPath);
        } else {
          // Unknown, could be reload or same-page
          callback?.('forward', newPath);
        }
        isPopState.value = false;
      } else {
        // Manual navigation via router
        // Add new route to history
        currentIndex.value++;
        history.splice(currentIndex.value); // Clear forward stack
        history.push(newPath);
        callback?.('forward', newPath);
      }
    }
  );
}
